/**
 * ProtectedRoute — only renders children if the user is authenticated.
 *
 * Routing rules:
 *   - not authenticated      → /login (with location preserved in state.from)
 *   - authenticated, no user → /signup  (must complete the signup wizard first)
 *   - authenticated, user    → render children
 *
 * The "no user" branch enforces an invariant we want everywhere except
 * the signup page itself: a user with an access token but no profile
 * has no business on /dashboard or any other protected area. They
 * finished OTP but bailed before completing signup, and we shouldn't
 * let them URL-bar their way past the wizard. The /signup route is
 * the explicit exception (otherwise we'd redirect in a loop).
 *
 * This complements PublicRoute, which redirects no-profile users TO
 * /signup when they hit /login or /otp. Together they make /signup
 * the mandatory next stop for any half-authenticated user.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

/** @param {{ children: React.ReactNode }} props */
export function ProtectedRoute({ children }) {
  // Whether the server has actually answered "does this phone have a profile".
  // Redirecting on `!user` alone sends registered clients to the signup wizard
  // whenever the profile fetch is still in flight or has failed.
  const { isAuthenticated, user, profileLoaded } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    // If this is a clinic capture deep-link (/face-capture?invite=…), remember
    // the invite before bouncing to login, so we can return the user to capture
    // after OTP. More reliable than reading the URL only at app boot.
    try {
      const inv = new URLSearchParams(location.search).get('invite');
      if (inv && location.pathname.startsWith('/face-capture')) {
        sessionStorage.setItem('pendingCaptureInvite', inv);
      }
      // Same idea for a support-sent re-run link (/rerun/<id>): it arrives by
      // SMS, so the client is very often not signed in on this device. Without
      // remembering it, OTP drops them on the dashboard and the link they were
      // sent is gone.
      const rr = location.pathname.match(/^\/rerun\/([^/]+)/);
      if (rr) {
        sessionStorage.setItem('pendingRerunId', rr[1]);
      }
    } catch { /* ignore */ }
    // Preserve where the user was trying to go so we can return them after login.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Authenticated but no profile yet → push to /signup, unless that's where
  // they already are, OR they are walking the FREE part of a consultation.
  //
  // A pre-signup guest arrives from the landing page having only verified a
  // phone number. Demanding a profile before they can capture or see what we
  // detected would put the wall back in exactly the place the free stage
  // exists to remove. The profile is required at PAYMENT, and the preview's
  // call to action changes to "complete your profile" there.
  const FREE_STAGE = [/^\/face-capture/, /^\/consultations\/[^/]+\/preview/, /^\/start/];
  const inFreeStage = FREE_STAGE.some((re) => re.test(location.pathname));

  // Only redirect once we KNOW there is no profile. While it is unknown the
  // page renders; any call it makes will 401 and the interceptor will sort the
  // session out, which is far better than bouncing an existing client into a
  // signup form they already completed.
  if (profileLoaded && !user && location.pathname !== '/signup' && !inFreeStage) {
    return <Navigate to="/signup" replace />;
  }

  return children;
}

export default ProtectedRoute;
